import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent
} from '@angular/material/autocomplete';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TrackingService } from 'src/app/helpers/services/tracking.service';
import { itemService } from 'src/app/helpers/services/main.service';
import decode from 'jwt-decode';
import { Observable, Subject, Subscription } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { getErrorMessage } from '../../helpers/api-error';

export interface FactoryProdOrderRow {
  id: number;
  prodOrderNo: string;
  prodReferenceNo?: string | null;
  vendorCode?: string;
  material_arrival_date?: string | null;
  mass_prod_start_date?: string | null;
  assembly_start_date?: string | null;
  actual_prod_complete?: string | null;
  packing_start_date?: string | null;
  qc_inspection_date?: string | null;
  actual_etd?: string | null;
}

export interface FactoryListRow {
  id: number;
  factory_name: string | null;
  factory_nickname: string | null;
  sort_order: number | null;
}

export interface FactorySampleOfferRow {
  sample_id_pk: number;
  sample_name?: string | null;
  quantity?: number | null;
  fob_cost?: number | null;
  office_location?: string | null;
  status?: string | null;
  notes?: string | null;
}

/** Production orders loaded into the PO dropdown per factory; the hint shows "x of y" past this. */
export const FACTORY_PROD_ORDER_LOAD_LIMIT = 1000;

@Component({
  selector: 'app-factory',
  templateUrl: './factory.component.html',
  styleUrls: ['./factory.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatSnackBarModule
  ]
})
export class FactoryComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  // One in-flight request per list: a newer factory selection cancels the older
  // request so a slow earlier response can never overwrite the current rows.
  private ordersSub?: Subscription;
  private offersSub?: Subscription;
  private licensingSub?: Subscription;

  factoryForm: FormGroup;
  sampleOfferForm: FormGroup;
  /** Searchable factory field (value is string while typing, or FactoryListRow when picked). */
  factorySearchCtrl = new FormControl<string | FactoryListRow>('');
  filteredFactories$: Observable<FactoryListRow[]>;

  prodOrders: FactoryProdOrderRow[] = [];
  // T38: read-only licensing status of the items on the selected PO.
  licensingItems: { itemNo: string; item_num_id: string; licensingStatus: string | null }[] = [];
  licensingLoading = false;
  factories: FactoryListRow[] = [];
  loadingList = false;
  loadingFactories = false;
  saving = false;
  savingOffer = false;
  loadingOffers = false;
  isVendor = false;
  private vendorFactoryId: number | null = null;
  recentOffers: FactorySampleOfferRow[] = [];
  /** Total production orders matching filters (getProdOrderHeader-style pagination). */
  prodOrderTotalCount = 0;

  readonly displayFactoryFn = (v: FactoryListRow | string | null): string => {
    if (v == null) {
      return '';
    }
    if (typeof v === 'string') {
      return v;
    }
    return this.factoryLabel(v);
  };

  constructor(
    private fb: FormBuilder,
    private tracking: TrackingService,
    private coreApi: itemService,
    private snackBar: MatSnackBar
  ) {
    this.factoryForm = this.fb.group({
      factoryId: [null as number | null, Validators.required],
      prodOrderId: [null as number | null, Validators.required],
      materialArrivalDate: [null as Date | null],
      massProdStartDate: [null as Date | null],
      assemblyStartDate: [null as Date | null],
      actualProdComplete: [null as Date | null],
      packingStartDate: [null as Date | null],
      qcInspectionDate: [null as Date | null],
      actualEtd: [null as Date | null]
    });

    this.sampleOfferForm = this.fb.group({
      destinationOffice: ['Ningbo', Validators.required],
      sampleName: ['', Validators.required],
      quantity: [null as number | null],
      fobCost: [null as number | null],
      notes: ['']
    });

    this.filteredFactories$ = this.factorySearchCtrl.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterFactoryList(this.factorySearchQueryFromValue(value)))
    );
  }

  ngOnInit(): void {
    this.readTokenContext();
    this.loadingFactories = true;
    this.coreApi.listFactories().pipe(takeUntil(this.destroy$)).subscribe({
      next: (list) => {
        const full = list || [];
        if (this.isVendor) {
          if (this.vendorFactoryId != null) {
            this.factories = full.filter((f) => f.id === this.vendorFactoryId);
          } else {
            this.factories = [];
          }
        } else {
          this.factories = full;
        }
        this.loadingFactories = false;
        this.applyVendorFactoryPreset();
        this.applyFactorySearchDisabledState();
        this.loadOrders();
        this.loadSampleOffers();
      },
      error: (err) => {
        this.loadingFactories = false;
        this.applyFactorySearchDisabledState();
        console.error('[where: FactoryComponent.ngOnInit]', err);
        this.snackBar.open(`Could not load factories: ${getErrorMessage(err, err?.url)} [where: FactoryComponent.ngOnInit]`, 'Dismiss', { duration: 5000 });
        this.loadOrders();
        this.loadSampleOffers();
      }
    });
  }

  private applyFactorySearchDisabledState(): void {
    if (this.loadingFactories || (this.isVendor && this.factories.length <= 1)) {
      this.factorySearchCtrl.disable({ emitEvent: false });
    } else {
      this.factorySearchCtrl.enable({ emitEvent: false });
    }
  }

  private factorySearchQueryFromValue(value: string | FactoryListRow | null): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return this.factoryLabel(value);
  }

  private filterFactoryList(query: string): FactoryListRow[] {
    const q = query.trim().toLowerCase();
    if (!q) {
      return [...this.factories];
    }
    return this.factories.filter((f) => {
      const label = this.factoryLabel(f).toLowerCase();
      const name = (f.factory_name || '').toLowerCase();
      const nick = (f.factory_nickname || '').toLowerCase();
      return (
        label.includes(q) ||
        name.includes(q) ||
        nick.includes(q) ||
        String(f.id).includes(q)
      );
    });
  }

  private setFactorySelection(f: FactoryListRow | null): void {
    if (!f) {
      this.factoryForm.patchValue({ factoryId: null }, { emitEvent: false });
      this.factorySearchCtrl.setValue('', { emitEvent: false });
      return;
    }
    this.factoryForm.patchValue({ factoryId: f.id }, { emitEvent: false });
    this.factorySearchCtrl.setValue(f, { emitEvent: false });
  }

  onFactoryOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const f = event.option.value as FactoryListRow;
    if (f) {
      this.setFactorySelection(f);
      this.onFactoryChange();
    }
  }

  onFactorySearchBlur(): void {
    const raw = this.factorySearchCtrl.value;
    const q = this.factorySearchQueryFromValue(raw).trim();
    const id = this.factoryForm.get('factoryId')?.value;
    const selected = id != null ? this.factories.find((x) => x.id === id) : null;
    if (selected && this.factoryLabel(selected) === q) {
      return;
    }
    const exact =
      q === ''
        ? null
        : this.factories.find((x) => this.factoryLabel(x) === q);
    if (exact) {
      this.setFactorySelection(exact);
      this.onFactoryChange();
    } else {
      this.setFactorySelection(null);
      this.loadOrders();
      this.loadSampleOffers();
    }
  }

  private readTokenContext(): void {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }
      const p = decode(token) as { role?: string; factory_id?: number };
      this.isVendor = p.role === 'vendor';
      if (p.role === 'vendor' && p.factory_id != null && !Number.isNaN(Number(p.factory_id))) {
        this.vendorFactoryId = Number(p.factory_id);
      }
    } catch (err: any) { console.error('[where: FactoryComponent.readTokenContext] could not decode the sign-in token', err);
      /* ignore */
    }
  }

  private applyVendorFactoryPreset(): void {
    if (this.isVendor && this.vendorFactoryId != null && this.factories.some((f) => f.id === this.vendorFactoryId)) {
      const f = this.factories.find((x) => x.id === this.vendorFactoryId)!;
      this.setFactorySelection(f);
    }
  }

  ngOnDestroy(): void {
    this.ordersSub?.unsubscribe();
    this.offersSub?.unsubscribe();
    this.licensingSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadOrders(): void {
    this.ordersSub?.unsubscribe();
    const fid = this.factoryForm.get('factoryId')?.value;
    if (fid === null || fid === undefined) {
      this.prodOrders = [];
      this.prodOrderTotalCount = 0;
      this.factoryForm.patchValue({
        prodOrderId: null,
        materialArrivalDate: null,
        massProdStartDate: null,
        assemblyStartDate: null,
        actualProdComplete: null,
        packingStartDate: null,
        qcInspectionDate: null,
        actualEtd: null
      });
      if (!this.factorySearchQueryFromValue(this.factorySearchCtrl.value).trim()) {
        this.factorySearchCtrl.setValue('', { emitEvent: false });
      }
      this.loadingList = false;
      return;
    }

    this.loadingList = true;
    const factoryIdParam = Number(fid);
    const startRow = 0;
    const endRow = FACTORY_PROD_ORDER_LOAD_LIMIT;

    this.ordersSub = this.tracking
      .getFactoryProdOrderList(factoryIdParam, {
        startRow,
        endRow,
        filterModel: {}
      })
      .subscribe({
      next: (res: { rows?: FactoryProdOrderRow[]; totalCount?: number }) => {
        this.prodOrders = res?.rows || [];
        this.prodOrderTotalCount = res?.totalCount ?? this.prodOrders.length;
        this.loadingList = false;
        this.factoryForm.patchValue({
          prodOrderId: null,
          materialArrivalDate: null,
          massProdStartDate: null,
          assemblyStartDate: null,
          actualProdComplete: null,
          packingStartDate: null,
          qcInspectionDate: null,
          actualEtd: null
        });
      },
      error: (err) => {
        this.loadingList = false;
        console.error('[where: FactoryComponent.loadOrders]', err);
        this.snackBar.open(`Could not load production orders: ${getErrorMessage(err, err?.url)} [where: FactoryComponent.loadOrders]`, 'Dismiss', { duration: 5000 });
      }
    });
  }

  loadSampleOffers(): void {
    this.offersSub?.unsubscribe();
    const fid = this.factoryForm.get('factoryId')?.value;
    if (fid === null || fid === undefined) {
      this.recentOffers = [];
      this.loadingOffers = false;
      return;
    }

    this.loadingOffers = true;
    this.offersSub = this.tracking.getSamples({
      startRow: 0,
      endRow: 10,
      filterModel: {
        origin: { filterType: 'text', type: 'equals', filter: 'factory_offer' },
        factory_id_fk: { filterType: 'number', type: 'equals', filter: Number(fid) }
      }
    }).subscribe({
      next: (res: { rows?: FactorySampleOfferRow[] }) => {
        this.recentOffers = res?.rows || [];
        this.loadingOffers = false;
      },
      error: () => {
        this.recentOffers = [];
        this.loadingOffers = false;
      }
    });
  }

  onFactoryChange(): void {
    this.factoryForm.patchValue({
      prodOrderId: null,
      materialArrivalDate: null,
      massProdStartDate: null,
      assemblyStartDate: null,
      actualProdComplete: null,
      packingStartDate: null,
      qcInspectionDate: null,
      actualEtd: null
    });
    this.loadOrders();
    this.loadSampleOffers();
  }

  factoryLabel(f: FactoryListRow): string {
    return (f.factory_name || f.factory_nickname || `Factory #${f.id}`).trim();
  }

  /** Dropdown label: prod reference (business key), same field prod tracking uses; fallback for legacy rows. */
  productionOrderLabel(o: FactoryProdOrderRow): string {
    const ref = (o.prodReferenceNo || '').trim();
    if (ref) {
      return ref;
    }
    const po = (o.prodOrderNo || '').trim();
    if (po) {
      return po;
    }
    return `Order #${o.id}`;
  }

  onProductionOrderChange(): void {
    const id = this.factoryForm.get('prodOrderId')?.value;
    const row = this.prodOrders.find((o) => o.id === id);
    if (!row) {
      this.licensingItems = [];
      return;
    }
    this.loadLicensingStatus(id);
    this.factoryForm.patchValue({
      materialArrivalDate: this.parseYmd(row.material_arrival_date),
      massProdStartDate: this.parseYmd(row.mass_prod_start_date),
      assemblyStartDate: this.parseYmd(row.assembly_start_date),
      actualProdComplete: this.parseYmd(row.actual_prod_complete),
      packingStartDate: this.parseYmd(row.packing_start_date),
      qcInspectionDate: this.parseYmd(row.qc_inspection_date),
      actualEtd: this.parseYmd(row.actual_etd)
    });
  }

  // T38: load the licensing status of the items on the selected PO. The endpoint
  // is factory-scoped server-side, so this only ever returns the caller's own order.
  private loadLicensingStatus(poId: number): void {
    this.licensingItems = [];
    this.licensingLoading = true;
    this.licensingSub?.unsubscribe();
    this.licensingSub = this.tracking.getFactoryProdOrderLicensing(poId).subscribe({
      next: (res) => {
        this.licensingItems = res?.items || [];
        this.licensingLoading = false;
      },
      error: () => {
        // No licensing rows / not permitted — show nothing rather than an error.
        this.licensingItems = [];
        this.licensingLoading = false;
      }
    });
  }

  private parseYmd(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) {
      return null;
    }
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    return new Date(y, mo, d);
  }

  private fmtYmd(d: Date | null | undefined): string | null {
    if (!d) {
      return null;
    }
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  onSave(): void {
    if (this.factoryForm.invalid) {
      this.factoryForm.markAllAsTouched();
      const msg =
        this.factoryForm.get('factoryId')?.invalid
          ? 'Select a factory'
          : 'Select a production order';
      this.snackBar.open(msg, 'Dismiss', { duration: 4000 });
      return;
    }
    const v = this.factoryForm.getRawValue();
    this.saving = true;
    this.tracking
      .updateFactoryProdOrderDates({
        id: v.prodOrderId,
        material_arrival_date: this.fmtYmd(v.materialArrivalDate),
        mass_prod_start_date: this.fmtYmd(v.massProdStartDate),
        assembly_start_date: this.fmtYmd(v.assemblyStartDate),
        actual_prod_complete: this.fmtYmd(v.actualProdComplete),
        packing_start_date: this.fmtYmd(v.packingStartDate),
        qc_inspection_date: this.fmtYmd(v.qcInspectionDate),
        actual_etd: this.fmtYmd(v.actualEtd)
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.snackBar.open('Saved', 'Dismiss', { duration: 3000 });
          const id = v.prodOrderId;
          const row = this.prodOrders.find((o) => o.id === id);
          if (row) {
            row.material_arrival_date = this.fmtYmd(v.materialArrivalDate);
            row.mass_prod_start_date = this.fmtYmd(v.massProdStartDate);
            row.assembly_start_date = this.fmtYmd(v.assemblyStartDate);
            row.actual_prod_complete = this.fmtYmd(v.actualProdComplete);
            row.packing_start_date = this.fmtYmd(v.packingStartDate);
            row.qc_inspection_date = this.fmtYmd(v.qcInspectionDate);
            row.actual_etd = this.fmtYmd(v.actualEtd);
          }
        },
        error: (err) => {
          this.saving = false;
          console.error('[where: FactoryComponent.onSave]', err);
          const msg = `Could not save the production order dates: ${getErrorMessage(err, err?.url)} [where: FactoryComponent.onSave]`;
          this.snackBar.open(msg, 'Dismiss', { duration: 6000 });
        }
      });
  }

  onSubmitSampleOffer(): void {
    if (!this.factoryForm.get('factoryId')?.value) {
      this.snackBar.open('Select a factory before entering a sample offer', 'Dismiss', { duration: 4000 });
      return;
    }
    if (this.sampleOfferForm.invalid) {
      this.sampleOfferForm.markAllAsTouched();
      this.snackBar.open('Enter a sample name and destination', 'Dismiss', { duration: 4000 });
      return;
    }

    const v = this.sampleOfferForm.getRawValue();
    this.savingOffer = true;
    this.tracking.createSample({
      origin: 'factory_offer',
      sample_name: v.sampleName,
      quantity: v.quantity,
      fob_cost: v.fobCost,
      factory_id_fk: this.factoryForm.get('factoryId')?.value,
      office_location: v.destinationOffice,
      notes: v.notes
    }).subscribe({
      next: () => {
        this.savingOffer = false;
        this.snackBar.open('Sample offer submitted', 'Dismiss', { duration: 3000 });
        this.sampleOfferForm.reset({
          destinationOffice: 'Ningbo',
          sampleName: '',
          quantity: null,
          fobCost: null,
          notes: ''
        });
        this.loadSampleOffers();
      },
      error: (err) => {
        this.savingOffer = false;
        console.error('[where: FactoryComponent.onSubmitSampleOffer]', err);
        const msg = `Could not submit sample offer: ${getErrorMessage(err, err?.url)} [where: FactoryComponent.onSubmitSampleOffer]`;
        this.snackBar.open(msg, 'Dismiss', { duration: 6000 });
      }
    });
  }
}
